import type { LatLon } from '@trips/shared/geo';
import { type Sky as SkyModel, skyAt, skyGradientCss } from '@trips/shared/sky';
import { type CSSProperties, type ReactNode, useMemo } from 'react';
import s from './Sky.module.css';

/** Deterministic star field so the sky doesn't shimmer between renders. */
function stars(seed: number, count: number) {
  let x = seed || 1;
  const rand = () => {
    x = (x * 16807) % 2147483647;
    return (x - 1) / 2147483646;
  };
  return Array.from({ length: count }, () => ({
    cx: rand() * 100,
    cy: rand() * 70,
    r: 0.25 + rand() * 0.45,
    o: 0.4 + rand() * 0.6,
  }));
}

interface SkyProps {
  /** Where the sky is: the place I'm at, or a flight's position. */
  place: LatLon | null | undefined;
  at: number;
  className?: string;
  children?: ReactNode;
  as?: 'div' | 'section' | 'article';
  style?: CSSProperties;
  /** Lets a shared-element transition find this card. */
  transitionName?: string;
}

export function skyFor(place: LatLon | null | undefined, at: number): SkyModel {
  // Without a place, use a calm evening sky over the Gulf coast.
  return skyAt(place ?? { lat: 26.5, lon: -81.8 }, at);
}

export function SkyCard({
  place,
  at,
  className,
  children,
  as: Tag = 'div',
  style,
  transitionName,
}: SkyProps) {
  // Recompute every 5 minutes of `at`; the sky changes slowly.
  const bucket = Math.floor(at / 300_000);
  const lat = place?.lat;
  const lon = place?.lon;
  const sky = useMemo(
    () => skyFor(lat === undefined || lon === undefined ? null : { lat, lon }, bucket * 300_000),
    [lat, lon, bucket],
  );
  const field = useMemo(
    () => stars(Math.round(((lat ?? 0) + 90) * 1000 + (lon ?? 0)), 42),
    [lat, lon],
  );
  return (
    <Tag
      className={`${s.sky} ${className ?? ''}`}
      data-ink={sky.inkTone}
      style={
        {
          ...style,
          background: skyGradientCss(sky),
          '--sky-ink': sky.ink,
          '--sky-ink-2':
            sky.inkTone === 'light' ? 'rgb(255 255 255 / 0.84)' : 'rgb(14 26 46 / 0.78)',
          '--sky-wash':
            sky.inkTone === 'light' ? 'rgb(255 255 255 / 0.16)' : 'rgb(14 26 46 / 0.08)',
          '--sky-line':
            sky.inkTone === 'light' ? 'rgb(255 255 255 / 0.22)' : 'rgb(14 26 46 / 0.16)',
          viewTransitionName: transitionName,
        } as CSSProperties
      }
    >
      {sky.stars > 0.02 && (
        <svg
          className={s.stars}
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
          style={{ opacity: sky.stars }}
        >
          {field.map((st, i) => (
            <circle key={i} cx={st.cx} cy={st.cy} r={st.r * 0.35} fill="#fff" opacity={st.o} />
          ))}
        </svg>
      )}
      <div className={s.content}>{children}</div>
    </Tag>
  );
}
